import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Save, Loader2, Copy, Code2, ExternalLink, Palette, Check } from "lucide-react";

const PRESETS = [
  { id: "forest",  label: "Forest",  primary: "#15803d", accent: "#ea580c" },
  { id: "ocean",   label: "Ocean",   primary: "#0369a1", accent: "#e11d48" },
  { id: "royal",   label: "Royal",   primary: "#6d28d9", accent: "#d97706" },
  { id: "slate",   label: "Slate",   primary: "#334155", accent: "#0d9488" },
];

export default function AdminEmbed() {
  const [introText, setIntroText]         = useState("");
  const [primaryColor, setPrimaryColor]   = useState("#15803d");
  const [accentColor, setAccentColor]     = useState("#ea580c");
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);

  const embedUrl = `${window.location.origin}/embed`;

  const iframeCode = `<iframe
  src="${embedUrl}"
  width="100%"
  height="800"
  frameborder="0"
  style="border: none; max-width: 900px; display: block;"
  title="Accessible Bike Booking"
></iframe>`;

  useEffect(() => {
    api.getSettings()
      .then((r) => {
        const ec = r.data.embed_config || {};
        setIntroText(ec.intro_text || "");
        setPrimaryColor(ec.primary_color || "#15803d");
        setAccentColor(ec.accent_color || "#ea580c");
      })
      .catch(() => toast.error("Failed to load embed settings"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSettings({ embed_config: { intro_text: introText, primary_color: primaryColor, accent_color: accentColor } });
      toast.success("Embed settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(iframeCode);
    toast.success("Embed code copied to clipboard");
  };

  const applyPreset = (preset) => {
    setPrimaryColor(preset.primary);
    setAccentColor(preset.accent);
  };

  const activePreset = PRESETS.find((p) => p.primary === primaryColor && p.accent === accentColor);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-8 h-8 animate-spin text-green-700" />
    </div>
  );

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Embed on Site</h1>
          <p className="text-slate-500 mt-1">Generate an embed code to display the booking calendar on your WordPress site.</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-embed-config-btn"
          className="gap-2 bg-[#15803d] hover:bg-green-800 text-white rounded-full px-6"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Intro text */}
      <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Calendar Introduction</h2>
          <p className="text-slate-500 text-sm mt-0.5">Text shown above the calendar in the embedded view. Leave blank to hide.</p>
        </div>
        <div className="p-6">
          <Label className="font-semibold text-slate-700 mb-2 block">Introduction Text</Label>
          <textarea
            data-testid="embed-intro-input"
            value={introText}
            onChange={(e) => setIntroText(e.target.value)}
            rows={3}
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-green-600 focus:bg-white resize-none transition-colors"
            placeholder="e.g. Choose a date and time for your accessible bike booking."
          />
          <p className="text-xs text-slate-400 mt-1.5">{introText.length} characters</p>
        </div>
      </div>

      {/* Colour scheme */}
      <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
          <Palette className="w-5 h-5 text-green-700" />
          <div>
            <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Colour Scheme</h2>
            <p className="text-slate-500 text-sm mt-0.5">Customise the embedded calendar to match your website's branding.</p>
          </div>
        </div>
        <div className="p-6 space-y-6">
          {/* Presets */}
          <div>
            <Label className="font-semibold text-slate-700 mb-3 block">Theme Presets</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {PRESETS.map((preset) => {
                const isActive = activePreset?.id === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    data-testid={`preset-${preset.id}`}
                    className={`relative rounded-xl border-2 p-3 text-left transition-all ${isActive ? "border-green-500 bg-green-50/50" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    {isActive && (
                      <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </span>
                    )}
                    <div className="flex gap-1.5 mb-2">
                      <span className="w-6 h-6 rounded-full border border-white shadow-sm" style={{ backgroundColor: preset.primary }} />
                      <span className="w-6 h-6 rounded-full border border-white shadow-sm" style={{ backgroundColor: preset.accent }} />
                    </div>
                    <p className="text-xs font-semibold text-slate-700">{preset.label}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom pickers */}
          <div className="border-t border-slate-100 pt-5">
            <Label className="font-semibold text-slate-700 mb-3 block">Custom Colours</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500 uppercase tracking-wide mb-2 block">Primary Colour</Label>
                <p className="text-xs text-slate-400 mb-2">Calendar highlights, slot borders, intro banner</p>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    data-testid="primary-color-picker"
                    className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5 bg-white"
                  />
                  <Input
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    data-testid="primary-color-input"
                    className="h-10 font-mono text-sm bg-slate-50 border-slate-200 max-w-36"
                    placeholder="#15803d"
                  />
                  <span className="w-8 h-8 rounded-full border border-white shadow" style={{ backgroundColor: primaryColor }} />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase tracking-wide mb-2 block">Accent Colour</Label>
                <p className="text-xs text-slate-400 mb-2">Submit button, call-to-action elements</p>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    data-testid="accent-color-picker"
                    className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5 bg-white"
                  />
                  <Input
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    data-testid="accent-color-input"
                    className="h-10 font-mono text-sm bg-slate-50 border-slate-200 max-w-36"
                    placeholder="#ea580c"
                  />
                  <span className="w-8 h-8 rounded-full border border-white shadow" style={{ backgroundColor: accentColor }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Embed code */}
      <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Code2 className="w-5 h-5 text-green-700" />
            <div>
              <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Embed Code</h2>
              <p className="text-slate-500 text-sm mt-0.5">Copy and paste into an HTML block in WordPress.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => window.open(embedUrl, "_blank")} data-testid="preview-embed-btn"
              className="gap-1.5 border-green-200 text-green-700 hover:bg-green-50">
              <ExternalLink className="w-3.5 h-3.5" /> Preview
            </Button>
            <Button onClick={handleCopy} data-testid="copy-embed-code-btn"
              className="gap-2 bg-[#15803d] hover:bg-green-800 text-white rounded-full px-5">
              <Copy className="w-4 h-4" /> Copy Code
            </Button>
          </div>
        </div>
        <div className="p-6">
          <pre data-testid="embed-code-block"
            className="bg-slate-900 text-green-300 rounded-xl p-5 text-sm overflow-x-auto font-mono leading-relaxed select-all whitespace-pre-wrap">
            {iframeCode}
          </pre>
        </div>
      </div>

      {/* Tips */}
      <div className="bg-blue-50 rounded-2xl border border-blue-100 p-5">
        <h3 className="font-semibold text-slate-700 mb-3 text-sm">WordPress Usage Tips</h3>
        <ul className="text-sm text-slate-600 space-y-1.5 list-disc list-inside">
          <li>In the WordPress editor, add an <strong>HTML block</strong> and paste the code above.</li>
          <li>Adjust the <code className="bg-white border border-slate-200 px-1 rounded text-xs">height</code> (currently 800) if the calendar is cut off — try 850 or 900.</li>
          <li>Colour changes take effect immediately — no need to update the embed code.</li>
          <li>Bookings made through the embed are processed identically to the main booking page.</li>
        </ul>
      </div>
    </div>
  );
}
